import { useMemo, useRef } from 'react';
import { css } from '@emotion/css';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { PersonOutline, SearchOutline } from '@lyearn/icons';
import { SidebarEmptyState } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box, CounterPill, Stack, Typography } from '@lyearn/ui';
import AsyncView from '@/components/AsyncView';
import Loader from '@/components/Loader';
import VirtualizedList from '@/components/VirtualizedList';
import { ContentEntity, GroupTypeEnum, UserType } from '@/types/schema';

import Search from '../../../admin/components/Header/Search';
import { SearchBarRef } from '../../../admin/components/Header/types';
import useGroupActions from '../../views/GroupDetails/hooks/useGroupActions';
import useGroupItems from '../../views/GroupDetails/hooks/useGroupItems';
import ListItemContent from './ListItemContent';

//ref:packages/apps/academy/src/@/components/FilterNew/components/Body/index.tsx
const MAX_POPOVER_HEIGHT = 480;
const MIN_POPOVER_HEIGHT = 280;

const filterBodyStyle = (height: number) => css`
  height: ${height >= MAX_POPOVER_HEIGHT ? MAX_POPOVER_HEIGHT : height}px;
`;

function GroupMemberList({
  groupId,
  title,
  count,
}: {
  groupId: string;
  title: string;
  count: number;
}) {
  const searchRef = useRef<SearchBarRef>(null);
  const { t } = useTranslation('Group');

  const type = GroupTypeEnum.UserCollection;
  const {
    state: { searchText },
    methods: { setSearchText },
  } = useGroupActions({ groupId, type });

  const { data, isFetchingMore, isFetching, fetchMore } = useGroupItems({
    groupId,
    type,
    searchText,
  });

  const clearSearch = useEvent(() => {
    searchRef.current?.clear();
  });

  const memoizedBodyHeight = useMemo(() => {
    //130 when title is large
    const height = 130 + count * 54;
    if (height < MIN_POPOVER_HEIGHT) {
      return filterBodyStyle(MIN_POPOVER_HEIGHT);
    }

    if (!data?.length) {
      return filterBodyStyle(380);
    }
    return filterBodyStyle(height);
  }, [count, data?.length]);

  return (
    <Stack
      className={cn('px-12 w-[320px]', count ? memoizedBodyHeight : 'min-h-[280px]')}
      direction="col"
      onClick={(e) => {
        e.stopPropagation();
      }}>
      <Stack className="w-full flex flex-col pt-16 top-0 sticky z-30 bg-bg-primary">
        <Stack className="ps-8 w-full" gapX={22} justifyContent="between">
          <Typography numberOfLines={2} variant="label-02">
            {title}
          </Typography>
          <CounterPill appearance="primary" label={count} size="small" />
        </Stack>
        <Search
          ref={searchRef}
          className="!px-0 !pb-8 !-mx-0 !mb-0 !pt-12"
          placeholder={t('Common:Search')}
          searchText={searchText ?? ''}
          setSearch={setSearchText}
        />
      </Stack>
      <Stack className="flex flex-1 flex-col overflow-auto">
        <AsyncView
          data={data}
          fetching={isFetching}
          renderData={(data) => (
            <VirtualizedList
              ListFooterComponent={
                isFetchingMore ? (
                  <Box className="h-96">
                    <Loader showLabel={false} />
                  </Box>
                ) : null
              }
              data={data}
              endReached={fetchMore}
              itemContent={(_: number, content: UserType | ContentEntity) => (
                <ListItemContent item={content} />
              )}
              useWindowScroll={false}
            />
          )}
          renderEmptyState={() =>
            count ? (
              <SidebarEmptyState
                Icon={SearchOutline}
                className="mt-32 mx-auto"
                description={t('Group:Details.NoSearchResults')}
                primaryAction={{
                  label: t('Common:Actions.Clear'),
                  onClick: clearSearch,
                }}
                width="narrow"
              />
            ) : (
              <SidebarEmptyState
                Icon={PersonOutline}
                className="mt-32 mx-auto"
                description={t('Group:Details.NoLearners')}
                width="narrow"
              />
            )
          }
        />
      </Stack>
    </Stack>
  );
}

export default GroupMemberList;
